import { sendEmailVerification } from 'firebase/auth'
import { auth } from '../config/firebase'
import { errorFromApi } from '../utils/customerError'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? '/api' : 'http://localhost:3003/api')

export const VERIFY_EMAIL_MESSAGE = 'Please verify your email to start designing.'

function rejected(data, status) {
  const err = errorFromApi(data, status, "We couldn't create your account. Please try again.")
  err.accountRejected = true
  return err
}

/** Asks the server before creating an account. A network failure does not block sign-up; the server still checks later. */
export async function checkSignupAllowed(email) {
  try {
    const res = await fetch(`${API_BASE_URL}/account/signup-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email })
    })
    const data = await res.json().catch(() => ({}))
    if (res.status === 403 || res.status === 429) throw rejected(data, res.status)
  } catch (err) {
    if (err.accountRejected) throw err
  }
}

/**
 * Records a new account with the server (disposable email and per-network sign-up checks).
 * Throws an error with accountRejected when the server refuses the account.
 */
export async function registerNewAccount(firebaseUser = auth.currentUser) {
  if (!firebaseUser) return null
  let res
  let data
  try {
    const token = await firebaseUser.getIdToken()
    res = await fetch(`${API_BASE_URL}/account/register`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
    })
    data = await res.json().catch(() => ({}))
  } catch (err) {
    console.warn('[account] register request failed; the server will check on first design:', err?.message)
    return null
  }
  if (res.status === 403 || res.status === 429) throw rejected(data, res.status)
  return res.ok ? data : null
}

export function needsEmailVerification(firebaseUser = auth.currentUser) {
  return Boolean(firebaseUser && !firebaseUser.isAnonymous && firebaseUser.emailVerified !== true)
}

export async function resendVerificationEmail(firebaseUser = auth.currentUser) {
  if (!firebaseUser) throw new Error('Please sign in to verify your email.')
  await sendEmailVerification(firebaseUser)
}

/** Reloads the account and refreshes the ID token so the server sees the verified email. */
export async function refreshEmailVerification(firebaseUser = auth.currentUser) {
  if (!firebaseUser) return false
  await firebaseUser.reload()
  const current = auth.currentUser || firebaseUser
  if (current.emailVerified) await current.getIdToken(true)
  return current.emailVerified === true
}
